import React, { createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../../../context/AppContext';

const PreviewContext = createContext(null);

export function PreviewProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    resumeData, setResumeData,
    template, handleTemplateChange,
    accentColor, setAccentColor,
    fontScale, setFontScale,
    fontFamily, setFontFamily,
    editMode, setEditMode,
    transitioning,
    shouldFlash,
    resumeScale, setManualZoom,
    resumeAreaRef, resumeInnerRef,
    effectiveZoom,
    photoInputRef,
    resumeFileInputRef,
    isUploadingPhoto,
    isUploadingResume,
    atsScore,
    variants, activeVariantId, activeCoverLetter, coverLetters,
    showJdModal, setShowJdModal,
    isBoosting, boostReport, setBoostReport, boostProgress,
    preBoostScore, postBoostScore, preBoostResume, handleAcceptBoost, handleRejectBoost,
    showDomainSelector, setShowDomainSelector, userDomain, setUserDomain,
    handleDownload,
    handleDownloadDocx,
    isDownloading,
    handleFieldUpdate,
    handleDeleteBullet,
    handleDeleteSkill,
    handleReorderSections,
    handleAIBoost,
    handleAIBoostConfirmed,
    handlePhotoUpload,
    handleResumeUpload,
    handleSelectVariant,
    handleDeleteVariant,
    handleVariantCreated,
    handleCreateVariant,
    canUndo, canRedo,
    handleUndo, handleRedo,
    SAMPLE_RESUME_DATA,
    coverTemplate, setCoverTemplate,
    coverLetterReloadTrigger,
    theme,
    authToken,
    setToast,
  } = useAppContext();

  // handleTabChange — lets child components (ChatWorkspace, etc.) navigate tabs
  // without importing AppContext directly.
  const handleTabChange = (tab) => {
    if (tab === 'home') {
      navigate('/app/dashboard');
    } else if (tab === 'assets_vault') {
      navigate('/app/vault');
    } else if (tab === 'tracker') {
      navigate('/app/tracker');
    } else if (['ats', 'jd_match', 'mock_interview', 'career_roadmap', 'audit'].includes(tab)) {
      const parts = location.pathname.split('/');
      const resId = (parts[1] === 'app' && parts[2] === 'resume') ? (parts[3] || 'master') : 'master';
      navigate(`/app/resume/${resId}/chat?tab=${tab}`);
    } else if (tab === 'ai' || tab === 'resume') {
      const parts = location.pathname.split('/');
      const resId = (parts[1] === 'app' && parts[2] === 'resume') ? (parts[3] || 'master') : 'master';
      navigate(`/app/resume/${resId}/chat`);
    } else if (tab === 'editor') {
      const parts = location.pathname.split('/');
      const resId = (parts[1] === 'app' && parts[2] === 'resume') ? (parts[3] || 'master') : 'master';
      navigate(`/app/resume/${resId}/editor`);
    } else if (tab === 'preview') {
      const parts = location.pathname.split('/');
      const resId = (parts[1] === 'app' && parts[2] === 'resume') ? (parts[3] || 'master') : 'master';
      navigate(`/app/resume/${resId}/preview`);
    }
  };

  const value = {
    resumeData,
    setResumeData,
    template,
    handleTemplateChange,
    accentColor,
    setAccentColor,
    fontScale,
    setFontScale,
    fontFamily,
    setFontFamily,
    editMode,
    setEditMode,
    transitioning,
    shouldFlash,
    resumeScale,
    setManualZoom,
    resumeAreaRef,
    resumeInnerRef,
    effectiveZoom,
    photoInputRef,
    resumeFileInputRef,
    isUploadingPhoto,
    isUploadingResume,
    atsScore,
    variants,
    coverLetters,
    activeVariantId,
    activeCoverLetter,
    showJdModal,
    setShowJdModal,
    isBoosting,
    boostReport,
    setBoostReport,
    boostProgress,
    preBoostScore,
    postBoostScore,
    preBoostResume,
    handleAcceptBoost,
    handleRejectBoost,
    showDomainSelector,
    setShowDomainSelector,
    userDomain,
    setUserDomain,
    handleDownload,
    handleDownloadDocx,
    isDownloading,
    handleFieldUpdate,
    handleDeleteBullet,
    handleDeleteSkill,
    handleReorderSections,
    handleAIBoost,
    handleAIBoostConfirmed,
    handlePhotoUpload,
    handleResumeUpload,
    handleSelectVariant,
    handleDeleteVariant,
    handleVariantCreated,
    handleCreateVariant,
    canUndo,
    canRedo,
    handleUndo,
    handleRedo,
    SAMPLE_RESUME_DATA,
    coverTemplate,
    setCoverTemplate,
    coverLetterReloadTrigger,
    theme,
    authToken,
    setToast,
    handleTabChange,
  };

  return (
    <PreviewContext.Provider value={value}>
      {children}
    </PreviewContext.Provider>
  );
}

export function usePreviewContext() {
  const context = useContext(PreviewContext);
  if (!context) {
    throw new Error('usePreviewContext must be used within a PreviewProvider');
  }
  return context;
}
